import { buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, MoreHorizontal } from 'lucide-react';
import * as React from 'react';

function Pagination({ className, ...props }: React.ComponentProps<'nav'>) {
    return (
        <nav
            role="navigation"
            aria-label="pagination"
            className={cn('mx-auto flex w-full justify-center', className)}
            {...props}
        />
    );
}

function PaginationContent({ className, ...props }: React.ComponentProps<'ul'>) {
    return (
        <ul
            className={cn('flex flex-row flex-wrap items-center gap-1', className)}
            {...props}
        />
    );
}

function PaginationItem(props: React.ComponentProps<'li'>) {
    return <li {...props} />;
}

type PaginationLinkProps = {
    isActive?: boolean;
    size?: 'default' | 'sm' | 'lg' | 'icon';
} & React.ComponentProps<'button'>;

/** Renders a button rather than an anchor: pagination here is component state, not navigation. */
function PaginationLink({ className, isActive, size = 'icon', ...props }: PaginationLinkProps) {
    return (
        <button
            type="button"
            aria-current={isActive ? 'page' : undefined}
            data-active={isActive ? 'true' : undefined}
            className={cn(buttonVariants({ variant: isActive ? 'default' : 'outline', size }), className)}
            {...props}
        />
    );
}

function PaginationFirst({ className, ...props }: PaginationLinkProps) {
    return (
        <PaginationLink
            aria-label="Go to first page"
            size="default"
            className={cn('gap-1 px-2.5', className)}
            {...props}
        >
            <ChevronsLeft />
            <span className="hidden sm:block">First</span>
        </PaginationLink>
    );
}

function PaginationPrevious({ className, ...props }: PaginationLinkProps) {
    return (
        <PaginationLink
            aria-label="Go to previous page"
            size="default"
            className={cn('gap-1 px-2.5', className)}
            {...props}
        >
            <ChevronLeft />
            <span className="hidden sm:block">Previous</span>
        </PaginationLink>
    );
}

function PaginationNext({ className, ...props }: PaginationLinkProps) {
    return (
        <PaginationLink
            aria-label="Go to next page"
            size="default"
            className={cn('gap-1 px-2.5', className)}
            {...props}
        >
            <span className="hidden sm:block">Next</span>
            <ChevronRight />
        </PaginationLink>
    );
}

function PaginationLast({ className, ...props }: PaginationLinkProps) {
    return (
        <PaginationLink
            aria-label="Go to last page"
            size="default"
            className={cn('gap-1 px-2.5', className)}
            {...props}
        >
            <span className="hidden sm:block">Last</span>
            <ChevronsRight />
        </PaginationLink>
    );
}

function PaginationEllipsis({ className, ...props }: React.ComponentProps<'span'>) {
    return (
        <span
            aria-hidden
            className={cn('flex size-10 items-center justify-center', className)}
            {...props}
        >
            <MoreHorizontal className="size-4" />
            <span className="sr-only">More pages</span>
        </span>
    );
}

export {
    Pagination,
    PaginationContent,
    PaginationEllipsis,
    PaginationFirst,
    PaginationItem,
    PaginationLast,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
};
